/**
 * Single home for "release this WZ from stock" — the DRAFT/CONFIRMED →
 * RELEASED transition that consumes the FIFO batches, freezes the document's
 * COGS. Later edits use the explicit audited correction command.
 *
 * Two entry points share it, and must stay identical in what they call, what
 * they say, and how they fail:
 *   - the save-time choice (askWarehouseSaveIntent → POST) on WZ create/edit;
 *   - the "Rozlicz wydanie" button on an already-saved draft.
 *
 * Release date (owner decision 2026-10-14, „zgodnie z wydaniem a nie z
 * utworzeniem”): every release sends the date of the release (`releaseDate`,
 * YYYY-MM-DD) — chosen in `askWZReleaseDate` or taken from the form — also on
 * every confirmation retry. The backend stores it on the WZ and books the
 * release cost in its month; without one it takes today.
 *
 * It also owns the WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY handshake. Over-delivery
 * is a WARNING, never a hard block (real logistics ships more than was ordered)
 * — the backend has no i18n, so it answers with an English message + `code` +
 * structured `lines`, which this module renders as a Polish confirmation naming
 * each affected line before re-sending with the operator's acknowledgement.
 */

import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { DatePicker, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import api from '../../services/api';
import { submitWithOperationalException } from '../operationalExceptions/operationalExceptionFlow';
import type { TFunction } from 'i18next';
import { getWZErrorMessage, isBlockingWZError } from './wzErrorMessages';
import { formatWarehouseQuantity } from './warehouseQuantityConversion';
import { formatUnit } from '../../utils/units';
import type { FeedbackApi } from '../../utils/feedback';

interface ExceedsOrderedQuantityLine {
  productName: string;
  unit: string;
  orderedQuantity: number;
  alreadyReleasedQuantity: number;
  addingQuantity: number;
  projectedQuantity: number;
}

interface UnitMismatchLine {
  productName: string;
  unit: string;
  orderUnits: string[];
  quantity: number;
  source: 'releasedSibling' | 'thisDocument';
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ModalLike {
  confirm: (config: {
    title: ReactNode;
    content?: ReactNode;
    width?: number;
    okText?: ReactNode;
    cancelText?: ReactNode;
    okType?: 'primary' | 'danger' | 'default' | 'dashed' | 'link' | 'text';
    onOk?: () => void;
    onCancel?: () => void;
  }) => unknown;
}

export interface ReleaseWZDocumentParams {
  wzId: string;
  /** Operator-facing document number, so the toast never reads as a bare "Wydano". */
  wzNumber?: string | null;
  /** `modal` from antd's `App.useApp()` — never the static `Modal.confirm`. */
  modal: ModalLike;
  t: TranslateFn;
  language: string;
  feedback: FeedbackApi;
  /**
   * A reason the operator already typed for these warnings in the calling
   * window (e.g. „Wystaw WZ” shows „brak zatwierdzonej faktury zakupu” up
   * front). Used only when every returned warning code is listed.
   */
  presetReason?: { codes: string[]; reason: string };
  /** Release date (YYYY-MM-DD) given at release; omitted = today on the backend. */
  releaseDate?: string | null;
}

interface ReleaseOptions {
  confirmExceedsOrderedQuantity?: boolean;
  confirmationFingerprint?: string;
  confirmOperationalException?: boolean;
  operationalExceptionFingerprint?: string;
  operationalExceptionReason?: string;
}

export async function releaseWZDocument(
  params: ReleaseWZDocumentParams,
  options?: ReleaseOptions,
): Promise<boolean> {
  const { wzId, wzNumber, modal, t, language, feedback, presetReason, releaseDate } = params;
  const datePayload = releaseDate ? { releaseDate } : {};

  let lastPayload: Record<string, unknown> = { ...options, ...datePayload, status: 'RELEASED' };
  try {
    const saved = await submitWithOperationalException(async payload => {
      lastPayload = payload;
      return api.put(`/wz/${wzId}/status`, payload);
    }, {
      ...options,
      ...datePayload,
      status: 'RELEASED',
      ...(options?.confirmExceedsOrderedQuantity ? { confirmExceedsOrderedQuantity: true } : {}),
      ...(options?.confirmationFingerprint ? { confirmationFingerprint: options.confirmationFingerprint } : {}),
    }, modal as Parameters<typeof submitWithOperationalException>[2], t as TFunction,
    presetReason?.reason ? { presetReason } : undefined);
    if (!saved) return false;
    feedback.success(wzNumber
      ? t('wz:success.statusUpdated', { wzNumber })
      : t('wz:success.statusUpdatedFallback'));
    return true;
  } catch (error: any) {
    const data = error?.response?.data;
    if (data?.code !== 'WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY') {
      // Every remaining release failure — no stock, unlinked lines, illegal
      // transition — is a state the operator must fix first, so the sentence
      // stays on screen instead of blinking away.
      feedback.error(getWZErrorMessage(error, t, 'wz:error.statusUpdateFailed'), {
        sticky: isBlockingWZError(error),
      });
      return false;
    }

    const confirmed = await confirmWZQuantityWarning(data, modal, t, language);

    if (!confirmed) {
      return false;
    }

    return releaseWZDocument(params, {
      ...lastPayload,
      confirmExceedsOrderedQuantity: true,
      confirmationFingerprint: data.confirmationFingerprint,
    });
  }
}

/** Calendar day the operator picks; future days are disabled (backend refuses them). */
export const isFutureReleaseDay = (day: Dayjs) => day.isAfter(dayjs(), 'day');

/** YYYY-MM-DD of a form date for the release request, or undefined. */
export const releaseDateOf = (day: Dayjs | null | undefined): string | undefined => (
  day && day.isValid() ? day.format('YYYY-MM-DD') : undefined
);

/**
 * Default day of the release picker: the given date (YYYY-MM-DD or ISO) when it
 * is valid and not in the future, else today.
 */
export const defaultReleaseDay = (preferred?: string | null): Dayjs => {
  const day = preferred ? dayjs(preferred) : null;
  return day && day.isValid() && !isFutureReleaseDay(day) ? day.startOf('day') : dayjs();
};

function WZReleaseDateField({ t, initial, onChange }: {
  t: TranslateFn; initial: Dayjs; onChange: (value: string | null) => void;
}) {
  const [value, setValue] = useState<Dayjs | null>(initial);
  return (
    <div>
      <p>{t('wz:confirm.releaseDescription')}</p>
      <label style={{ display: 'block' }}>
        <Typography.Text strong>{t('wz:fields.releaseDate')}</Typography.Text>
        <DatePicker
          data-testid="wz-release-date"
          aria-label={t('wz:fields.releaseDate')}
          style={{ display: 'block', width: 200, marginTop: 4 }}
          format="DD.MM.YYYY"
          value={value}
          allowClear={false}
          disabledDate={isFutureReleaseDay}
          onChange={(next) => { setValue(next); onChange(releaseDateOf(next) ?? null); }}
        />
      </label>
      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
        {t('wz:confirm.releaseDateHelp')}
      </Typography.Text>
    </div>
  );
}

/**
 * „Rozliczyć wydanie?” with the „Data wydania” picker (default `defaultDate`
 * when valid and not in the future, else today; no future days). Resolves the
 * chosen YYYY-MM-DD, or null when cancelled.
 */
export function askWZReleaseDate({ modal, t, defaultDate }: {
  modal: ModalLike; t: TranslateFn; defaultDate?: string | null;
}): Promise<string | null> {
  const initial = defaultReleaseDay(defaultDate);
  let chosen: string | null = initial.format('YYYY-MM-DD');
  return new Promise<string | null>((resolve) => {
    modal.confirm({
      title: t('wz:confirm.releaseTitle'),
      width: 480,
      content: <WZReleaseDateField t={t} initial={initial} onChange={(value) => { chosen = value; }} />,
      okText: t('wz:actions.settleRelease'),
      cancelText: t('common:cancel'),
      onOk: () => resolve(chosen),
      onCancel: () => resolve(null),
    });
  });
}

export async function confirmWZQuantityWarning(
  data: any, modal: ReleaseWZDocumentParams['modal'], t: ReleaseWZDocumentParams['t'], language: string,
): Promise<boolean> {
    const lines: ExceedsOrderedQuantityLine[] = Array.isArray(data.lines) ? data.lines : [];
    const unitMismatchLines: UnitMismatchLine[] = Array.isArray(data.unitMismatchLines)
      ? data.unitMismatchLines
      : [];

    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('wz:confirm.exceedsOrderedQuantityTitle'),
        width: 560,
        content: (
          <div>
            {lines.length > 0 && <p>{t('wz:confirm.exceedsOrderedQuantityIntro')}</p>}
            <ul>
              {lines.map((line, index) => (
                <li key={`${line.productName}-${index}`}>
                  {t('wz:confirm.exceedsOrderedQuantityLine', {
                    productName: line.productName,
                    ordered: formatWarehouseQuantity(line.orderedQuantity, line.unit, language),
                    alreadyReleased: formatWarehouseQuantity(line.alreadyReleasedQuantity, line.unit, language),
                    adding: formatWarehouseQuantity(line.addingQuantity, line.unit, language),
                    projected: formatWarehouseQuantity(line.projectedQuantity, line.unit, language),
                  })}
                </li>
              ))}
              {unitMismatchLines.map((line, index) => (
                <li key={`unit-${line.productName}-${index}`}>
                  {t('wz:confirm.unitMismatchLine', {
                    productName: line.productName,
                    quantity: formatWarehouseQuantity(line.quantity, line.unit, language),
                    orderUnits: line.orderUnits.map((unit) => formatUnit(unit, language)).join(', '),
                    source: t(`wz:confirm.unitMismatchSource.${line.source}`),
                  })}
                </li>
              ))}
            </ul>
            <p>{t('wz:confirm.exceedsOrderedQuantityFooter')}</p>
          </div>
        ),
        okText: t('wz:confirm.exceedsOrderedQuantityConfirm'),
        cancelText: t('common:cancel'),
        okType: 'danger',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

}
